import { View } from "@novorender/api";
import { useCallback, useRef } from "react";

import { useAppDispatch } from "app/redux-store-interactions";

import { renderActions } from "../render/renderSlice";

/**
 * Configuration for hover tracking behavior
 */
export interface MarkerDragHoverTrackingConfig {
    /**
     * Minimum time in ms between hover object updates (throttling for performance)
     * @default 50
     */
    throttleMs?: number;
    /**
     * Whether tracking is enabled
     * @default true
     */
    enabled?: boolean;
}

/**
 * Hook that manages hover object highlighting during marker drag/creation.
 * Tracks the object ID under the cursor and dispatches updates to render state
 * for surface highlighting with throttling for performance.
 *
 * @param view - The explorer view instance
 * @param config - Configuration options for hover tracking
 * @returns Object with methods to update and clear hover state
 *
 * @example
 * ```typescript
 * const { updateHoverObject, clearHoverObject } = useMarkerDragHoverTracking(view);
 *
 * // During pick operation
 * const pickResult = await view.pick(x, y);
 * updateHoverObject(pickResult?.objectId);
 *
 * // When drag ends
 * clearHoverObject();
 * ```
 */
export function useMarkerDragHoverTracking(view: View | undefined, config: MarkerDragHoverTrackingConfig = {}) {
    const { throttleMs = 50, enabled = true } = config;
    const dispatch = useAppDispatch();

    const lastHoverUpdateTimeRef = useRef<number>(0);
    const currentHoverObjectIdRef = useRef<number | undefined>(undefined);

    /**
     * Update the hover object if it has changed or enough time has passed (throttling).
     * Dispatches to render state for surface highlighting.
     *
     * @param objectId - The object ID from pick result, or undefined if no object
     */
    const updateHoverObject = useCallback(
        (objectId: number | undefined) => {
            if (!enabled || !view) {
                return;
            }

            const now = performance.now();
            const currentHoverObjectId = currentHoverObjectIdRef.current;

            // Check if objectId is valid and (different from current OR enough time has passed)
            if (
                objectId !== undefined &&
                objectId >= 0 &&
                (objectId !== currentHoverObjectId || now - lastHoverUpdateTimeRef.current > throttleMs)
            ) {
                currentHoverObjectIdRef.current = objectId;
                lastHoverUpdateTimeRef.current = now;

                // Dispatch to render state for surface highlighting
                dispatch(renderActions.setMarkerDragHoverObject(objectId));
            } else if (objectId === undefined || objectId < 0) {
                // Clear hover if no valid object
                if (currentHoverObjectId !== undefined) {
                    currentHoverObjectIdRef.current = undefined;
                    dispatch(renderActions.setMarkerDragHoverObject(undefined));
                }
            }
        },
        [enabled, view, dispatch, throttleMs],
    );

    /**
     * Clear the current hover object state.
     * Should be called when drag/creation ends.
     */
    const clearHoverObject = useCallback(() => {
        if (currentHoverObjectIdRef.current !== undefined) {
            currentHoverObjectIdRef.current = undefined;
            lastHoverUpdateTimeRef.current = 0;
            dispatch(renderActions.setMarkerDragHoverObject(undefined));
        }
    }, [dispatch]);

    return {
        updateHoverObject,
        clearHoverObject,
    };
}
